import { useEffect, useState } from "react";
import { getListaAlunos } from "@/services/report";
import { getAllEspecificas } from "@/services/especifica";
import { getTurma } from "@/services/turma";
import { Table, ConfigProvider, theme, Select, Button } from "antd";
import type { ColumnsType } from 'antd/es/table';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "@/styles/globals.css";
import logoImg from "@/assets/images/Logo.png";

interface Especifica {
  id: number;
  nome: string;
}

interface ListaAlunosItem {
  nomeAluno: string;
  nomeEspecifica: string;
  nomeTurma: string;
}

interface UnidadeOpcao {
  id: number;
  nome: string;
}

const getUnidadesDoUsuario = (): UnidadeOpcao[] => {
  try {
    const user = JSON.parse(localStorage.getItem('@MatriculaWeb:user') as any);
    const unidades = user?.usuario?.unidades ?? [];
    return unidades
      .map((u: any) => ({
        id: Number(u?.id ?? u?.Id),
        nome: String(u?.nome ?? u?.Nome ?? ''),
      }))
      .filter((u: UnidadeOpcao) => !Number.isNaN(u.id) && u.nome);
  } catch {
    return [];
  }
};

const getUnidadeLogadaId = (): number | undefined => {
  try {
    const unidade = JSON.parse(localStorage.getItem('UnidadeLogada') as any);
    const id = Number(unidade?.id ?? unidade?.Id);
    return Number.isNaN(id) ? undefined : id;
  } catch {
    return undefined;
  }
};

export default function ListaAlunos() {
  const [especificas, setEspecificas] = useState<Especifica[]>([]);
  const [turmas, setTurmas] = useState<{ id: number; nome: string }[]>([]);
  const [unidades, setUnidades] = useState<UnidadeOpcao[]>([]);
  const [filtroUnidade, setFiltroUnidade] = useState<number | undefined>(undefined);
  const [filtroEspecifica, setFiltroEspecifica] = useState("");
  const [filtroTurma, setFiltroTurma] = useState("");
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<ListaAlunosItem[]>([]);
  useEffect(() => {
    getTurma().then((res) => setTurmas(res.data || []));
    getAllEspecificas().then((res) => setEspecificas(res.data || []));
    setUnidades(getUnidadesDoUsuario());
    setFiltroUnidade(getUnidadeLogadaId());
  }, []);

  useEffect(() => {
    if (filtroTurma) {
      getAllEspecificas({ turmaId: filtroTurma }).then((res) => setEspecificas(res.data || []));
      setFiltroEspecifica("");
    } else {
      getAllEspecificas().then((res) => setEspecificas(res.data || []));
      setFiltroEspecifica("");
    }
  }, [filtroTurma]);

  const buscar = async () => {
    if (!filtroEspecifica) {
      return;
    }
    setLoading(true);
    const params: any = {};
    if (filtroTurma) {
      params.turmaId = filtroTurma;
    }
    if (filtroEspecifica) {
      params.especificaId = filtroEspecifica;
    }
    if (filtroUnidade) {
      params.unidadeId = filtroUnidade;
    }
    try {
      const res = await getListaAlunos(params);
      setDados(res.data || []);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        window.alert("Dados não encontrados para os filtros informados");
        setDados([]);
      } else {
        window.alert("Consulte o Suporte");
        setDados([]);
      }
    }
    setLoading(false);
  };
  const exportToExcel = () => {
    const exportData = dados.map(item => ({
      "Aluno": item.nomeAluno,
      "Específica": item.nomeEspecifica,
      "Turma": item.nomeTurma,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ListaAlunos");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "lista_alunos.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "portrait" });

    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 35;
    const logoHeight = 15;
    const logoX = 10;
    const logoY = 12;

    const drawLogo = () => {
      try {
        doc.addImage(logoImg as unknown as string, 'PNG', logoX, logoY, logoWidth, logoHeight);
      } catch {
        // Se a imagem falhar ao carregar, segue sem logo.
      }
    };

    drawLogo();

    // Título principal
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    
    // Buscar o nome da específica selecionada
    const idEspecificaSelecionada = Number(filtroEspecifica);
    const especificaSelecionada = especificas.find(e => e.id === idEspecificaSelecionada);
    const nomeEspecifica = especificaSelecionada?.nome || "Específica";
    
    const titulo = `Academia de ${nomeEspecifica}`;
    const textWidth = doc.getTextWidth(titulo);
    const x = (pageWidth - textWidth) / 2;
    const turmaSelecionada = turmas.find(t => t.id === Number(filtroTurma));
    const nomeTurma = filtroTurma ? turmaSelecionada?.nome : "Todas";
    
    doc.text(titulo, x, 20);
    const subtitulo = `Turma: ${nomeTurma} - ${unidades.find(u => u.id === filtroUnidade)?.nome || "Todas as Unidades"}`;
    
    // Campo de data
    const subTextWidth = doc.getTextWidth(subtitulo);
    doc.setFontSize(12);
    const subX = (pageWidth - subTextWidth) / 2;
    doc.setFont("helvetica", "normal");
    doc.text("Data: _____ / _____ / _______", pageWidth - 80, 35);
    doc.text(subtitulo, subX, 28);
    
    // Configurações da tabela
    const startY = 50;
    const rowHeight = 8;
    const colWidths = [15, 90, 90]; // Número, Nome do Aluno (reduzida), Assinatura (aumentada)
    const maxRowsPerPage = 30; // Máximo de linhas por página
    
    // Centralizar a tabela na página
    const tableWidth = colWidths.reduce((sum, width) => sum + width, 0); // Total: 195
    const marginLeft = (pageWidth - tableWidth) / 2; // Centralizar horizontalmente
    
    const drawHeader = (yPosition: number) => {
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      
      let currentX = marginLeft;
      
      // Número
      doc.rect(currentX, yPosition, colWidths[0], rowHeight);
      const numeroText = "Nº";
      const numeroTextWidth = doc.getTextWidth(numeroText);
      const numeroX = currentX + (colWidths[0] - numeroTextWidth) / 2;
      doc.text(numeroText, numeroX, yPosition + 5);
      
      currentX += colWidths[0];
      // Nome do Aluno
      doc.rect(currentX, yPosition, colWidths[1], rowHeight);
      const alunoText = "Nome do Aluno";
      const alunoTextWidth = doc.getTextWidth(alunoText);
      const alunoX = currentX + (colWidths[1] - alunoTextWidth) / 2;
      doc.text(alunoText, alunoX, yPosition + 5);
      
      currentX += colWidths[1];
      // Assinatura
      doc.rect(currentX, yPosition, colWidths[2], rowHeight);
      const assinaturaText = "Assinatura";
      const assinaturaTextWidth = doc.getTextWidth(assinaturaText);
      const assinaturaX = currentX + (colWidths[2] - assinaturaTextWidth) / 2;
      doc.text(assinaturaText, assinaturaX, yPosition + 5);
    };
    
    // Desenhar cabeçalho inicial
    drawHeader(startY);
    
    // Dados dos alunos
    doc.setFont("helvetica", "normal");
    let currentPage = 1;
    let currentRowInPage = 0;
    
    dados.forEach((item, index) => {
      // Verificar se precisa de nova página
      if (currentRowInPage >= maxRowsPerPage) {
        doc.addPage();
        currentPage++;
        currentRowInPage = 0;
        
        // Repetir logo, título e cabeçalho na nova página
        drawLogo();
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(titulo, x, 20);
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text("Data: _____ / _____ / _______", pageWidth - 80, 35);
        
        drawHeader(startY);
        doc.setFont("helvetica", "normal");
        doc.text(subtitulo, subX, 28);
      }
      
      const rowY = startY + rowHeight + (currentRowInPage * rowHeight);
      let currentX = marginLeft;
      
      // Número sequencial
      doc.rect(currentX, rowY, colWidths[0], rowHeight);
      const numeroSequencial = (index + 1).toString();
      const numeroSequencialWidth = doc.getTextWidth(numeroSequencial);
      const numeroSequencialX = currentX + (colWidths[0] - numeroSequencialWidth) / 2;
      doc.text(numeroSequencial, numeroSequencialX, rowY + 5);
      
      currentX += colWidths[0];
      // Nome do aluno
      doc.rect(currentX, rowY, colWidths[1], rowHeight);
      const nomeAluno = item.nomeAluno || "";
      // Truncar nome se for muito longo - ajustado para a nova largura da coluna
      const maxChars = 35;
      const nomeExibir = nomeAluno.length > maxChars ? nomeAluno.substring(0, maxChars - 3) + "..." : nomeAluno;
      doc.text(nomeExibir, currentX + 2, rowY + 5);
      
      currentX += colWidths[1];
      // Campo de assinatura (vazio)
      doc.rect(currentX, rowY, colWidths[2], rowHeight);
      
      currentRowInPage++;
    });

    doc.save("lista_alunos.pdf");
  };

  const columns: ColumnsType<ListaAlunosItem> = [
    { title: "Aluno", dataIndex: "nomeAluno", key: "nomeAluno" },
    { title: "Específica", dataIndex: "nomeEspecifica", key: "nomeEspecifica" },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Lista de Alunos</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-4 items-end mb-8">
          <Select
            value={filtroUnidade}
            onChange={(value) => setFiltroUnidade(value)}
            style={{ width: 220 }}
            placeholder="Unidade"
            options={unidades.map((u) => ({ value: u.id, label: u.nome }))}
          />
          <Select
            value={filtroTurma}
            onChange={setFiltroTurma}
            style={{ width: 200 }}
            allowClear
            placeholder="Turma"
            options={[
              { value: "", label: "Todas as Turmas" },
              ...turmas.map((t) => ({ value: t.id, label: t.nome })),
            ]}
          />
          <Select
            value={filtroEspecifica}
            onChange={setFiltroEspecifica}
            style={{ width: 200 }}
            allowClear
            placeholder="Especifica"
            options={[
              { value: "", label: "Selecione uma específica" },
              ...especificas.map((e) => ({ value: e.id, label: e.nome })),
            ]}
          />
          <Button type="primary" loading={loading} onClick={buscar} disabled={!filtroEspecifica}>
            Buscar
          </Button>
          <Button onClick={exportToExcel} disabled={dados.length === 0}>Exportar Excel</Button>
          <Button onClick={exportToPDF} disabled={dados.length === 0}>Exportar PDF</Button>
        </div>
        <Table
          columns={columns}
          dataSource={dados}
          rowKey={(row, idx) => `${row.nomeAluno}-${idx}`}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: "Nenhum dado encontrado." }}
        />
      </ConfigProvider>
    </>
  );
}
